import { createContext, useContext, useState, useCallback, useMemo, ReactNode, lazy, Suspense } from "react";
import { Volume2, VolumeX, Settings } from "lucide-react";
import { motion } from "framer-motion";
import { useSettings } from "@/engine/settingsStore";
import TypographyControl from "@/components/TypographyControl";

// Lazy: SettingsPanel only mounts when the user clicks the gear button.
// Keeps it out of the GlobalControls bundle (which is itself part of the
// Play.tsx bundle, so this also trims chronicle playthrough payloads).
const SettingsPanel = lazy(() => import("@/components/SettingsPanel"));

/* ── Visibility context ── */
interface GlobalControlsVisibility {
  visible: boolean;
  setVisible: (v: boolean) => void;
}

const VisibilityContext = createContext<GlobalControlsVisibility>({
  visible: true,
  setVisible: () => {},
});

export function useGlobalControlsVisibility() {
  return useContext(VisibilityContext);
}

export function GlobalControlsProvider({ children }: { children: ReactNode }) {
  const [visible, setVisible] = useState(false);
  // Memoise to prevent every consumer re-rendering on every unrelated
  // parent render. setVisible from useState is already stable; only
  // `visible` changes.
  const value = useMemo<GlobalControlsVisibility>(() => ({ visible, setVisible }), [visible]);
  return (
    <VisibilityContext.Provider value={value}>
      {children}
    </VisibilityContext.Provider>
  );
}

/* ── The controls overlay ── */
export default function GlobalControls() {
  const { visible } = useContext(VisibilityContext);
  const { settings, update } = useSettings();
  const [settingsOpen, setSettingsOpen] = useState(false);

  const toggleMusic = useCallback(() => {
    update({ musicMuted: !settings.musicMuted });
  }, [settings.musicMuted, update]);

  if (!visible) return null;

  return (
    <>
      <div className="fixed top-2 right-2 sm:top-3 sm:right-3 z-[200] flex items-center gap-[4px] sm:gap-[6px] rounded-sm p-[4px] sm:p-[6px] pointer-events-auto" style={{ background: "hsl(var(--background) / 0.85)", backdropFilter: "blur(8px)", border: "1px solid hsl(var(--border) / 0.3)", boxShadow: "0 2px 12px hsl(0 0% 0% / 0.25)" }}>
        {/* Reading typography (text size + column width) */}
        <TypographyControl size="md" side="bottom" align="end" />

        {/* Music toggle */}
        <motion.button
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.92 }}
          onClick={toggleMusic}
          className={`flex items-center justify-center w-[44px] h-[44px] sm:w-[36px] sm:h-[36px] rounded-sm border transition-all duration-300 ${
            settings.musicMuted
              ? "border-border/40 text-muted-foreground hover:text-foreground hover:border-border"
              : "bg-primary/10 border-primary/30 text-primary"
          }`}
          title={settings.musicMuted ? "Unmute music" : "Mute music"}
        >
          {settings.musicMuted ? <VolumeX className="w-[16px] h-[16px]" /> : <Volume2 className="w-[16px] h-[16px]" />}
        </motion.button>

        {/* Settings */}
        <motion.button
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.92 }}
          onClick={() => setSettingsOpen(true)}
          className="flex items-center justify-center w-[44px] h-[44px] sm:w-[36px] sm:h-[36px] rounded-sm border border-border/40 text-muted-foreground hover:text-foreground hover:border-border transition-all duration-300"
          title="Settings"
        >
          <Settings className="w-[16px] h-[16px]" />
        </motion.button>
      </div>

      <Suspense fallback={null}>
        <SettingsPanel open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      </Suspense>
    </>
  );
}
